import { isAllowedLlmUpstreamUrl } from "../lib/llmAllowedHosts";
import { putLlmDebugLog, truncateDebugText } from "./db";

export type LlmProvider = "openai" | "anthropic" | "gemini";
export type LlmApiStyle = "chat-completions" | "responses";

export interface LlmSettings {
  baseUrl: string;
  model: string;
  apiKey: string;
  useProxy: boolean;
  temperature: number;
  evolutionEnabled?: boolean;
  embedding?: EmbeddingRoute;
}

export interface LlmRoute extends LlmSettings {
  id?: string;
  provider?: LlmProvider;
  apiStyle?: LlmApiStyle;
  maxTokens?: number;
  timeoutMs?: number;
  retries?: number;
  extraHeaders?: Record<string, string>;
}

export interface LlmMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface LlmUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

export interface LlmResult {
  text: string;
  reasoningText: string;
  usage: LlmUsage | null;
  routeId: string;
  provider: LlmProvider;
  apiStyle: LlmApiStyle;
  responseId?: string;
}

export interface LlmCallOptions {
  signal?: AbortSignal;
  stream?: boolean;
  onToken?: (token: string) => void;
  responseFormat?: "text" | "json";
  metadata?: Record<string, unknown>;
  debug?: {
    featureId: string;
    archiveName?: string;
    turnId?: string;
  };
}

export interface EmbeddingRoute {
  baseUrl: string;
  apiKey: string;
  model: string;
  useProxy?: boolean;
  dimensions?: number;
  timeoutMs?: number;
}

export class LlmNoVisibleContentError extends Error {
  readonly reasoningText: string;
  readonly responseId?: string;
  constructor(reasoningText: string, responseId?: string) {
    super("模型请求成功，但没有返回可见正文");
    this.name = "LlmNoVisibleContentError";
    this.reasoningText = reasoningText;
    this.responseId = responseId;
  }
}

export class LlmHttpError extends Error {
  constructor(
    readonly status: number,
    readonly responseBody: string,
    readonly retryAfterMs: number | null,
  ) {
    super(
      `LLM 请求失败 (${status})${responseBody ? `: ${responseBody.slice(0, 500)}` : ""}`,
    );
    this.name = "LlmHttpError";
  }
}

const RETRYABLE_STATUS = new Set([408, 409, 425, 429, 500, 502, 503, 504]);

function abortError(): DOMException {
  return new DOMException("请求已取消", "AbortError");
}

function joinUrl(baseUrl: string, suffix: string): string {
  const base = baseUrl.trim().replace(/\/+$/, "");
  if (base.endsWith(suffix)) return base;
  if (/\/(chat\/completions|responses|messages|embeddings)$/.test(base)) {
    return base.replace(
      /\/(chat\/completions|responses|messages|embeddings)$/,
      suffix,
    );
  }
  return `${base}${suffix}`;
}

function inferProvider(route: LlmRoute): LlmProvider {
  if (route.provider) return route.provider;
  const host = safeHostname(route.baseUrl);
  if (host.includes("anthropic")) return "anthropic";
  if (host.includes("googleapis") || host.includes("aiplatform"))
    return "gemini";
  return "openai";
}

function inferApiStyle(route: LlmRoute, provider: LlmProvider): LlmApiStyle {
  if (route.apiStyle) return route.apiStyle;
  if (provider !== "openai") return "chat-completions";
  if (/\/responses\/?$/i.test(route.baseUrl)) return "responses";
  return "chat-completions";
}

function safeHostname(rawUrl: string): string {
  try {
    return new URL(rawUrl).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function endpointFor(
  route: LlmRoute,
  provider: LlmProvider,
  apiStyle: LlmApiStyle,
): string {
  if (provider === "anthropic") return joinUrl(route.baseUrl, "/messages");
  if (provider === "gemini") {
    const base = route.baseUrl.replace(/\/+$/, "");
    if (
      base.includes(":generateContent") ||
      base.includes(":streamGenerateContent")
    )
      return base;
    return `${base}/models/${encodeURIComponent(route.model)}:streamGenerateContent?alt=sse`;
  }
  return joinUrl(
    route.baseUrl,
    apiStyle === "responses" ? "/responses" : "/chat/completions",
  );
}

function buildOpenAiBody(
  route: LlmRoute,
  messages: LlmMessage[],
  apiStyle: LlmApiStyle,
  options: LlmCallOptions,
) {
  const common = {
    model: route.model,
    temperature: route.temperature,
    stream: options.stream !== false,
  };
  if (apiStyle === "responses") {
    return {
      ...common,
      input: messages.map((message) => ({
        role: message.role,
        content: [{ type: "input_text", text: message.content }],
      })),
      max_output_tokens: route.maxTokens,
      text:
        options.responseFormat === "json"
          ? { format: { type: "json_object" } }
          : undefined,
      metadata: options.metadata,
    };
  }
  return {
    ...common,
    messages,
    max_tokens: route.maxTokens,
    response_format:
      options.responseFormat === "json" ? { type: "json_object" } : undefined,
  };
}

function buildAnthropicBody(
  route: LlmRoute,
  messages: LlmMessage[],
  options: LlmCallOptions,
) {
  const system = messages
    .filter((message) => message.role === "system")
    .map((message) => message.content)
    .join("\n\n");
  return {
    model: route.model,
    system,
    messages: messages.filter((message) => message.role !== "system"),
    max_tokens: route.maxTokens ?? 4096,
    temperature: route.temperature,
    stream: options.stream !== false,
  };
}

function buildGeminiBody(
  route: LlmRoute,
  messages: LlmMessage[],
  options: LlmCallOptions,
) {
  const system = messages
    .filter((message) => message.role === "system")
    .map((message) => message.content)
    .join("\n\n");
  return {
    systemInstruction: system ? { parts: [{ text: system }] } : undefined,
    contents: messages
      .filter((message) => message.role !== "system")
      .map((message) => ({
        role: message.role === "assistant" ? "model" : "user",
        parts: [{ text: message.content }],
      })),
    generationConfig: {
      temperature: route.temperature,
      maxOutputTokens: route.maxTokens,
      responseMimeType:
        options.responseFormat === "json" ? "application/json" : "text/plain",
    },
  };
}

function buildHeaders(route: LlmRoute, provider: LlmProvider): Headers {
  const headers = new Headers({
    "Content-Type": "application/json",
    ...route.extraHeaders,
  });
  if (provider === "anthropic") {
    headers.set("x-api-key", route.apiKey);
    headers.set(
      "anthropic-version",
      route.extraHeaders?.["anthropic-version"] ?? "2023-06-01",
    );
  } else if (provider === "gemini") {
    headers.set("x-goog-api-key", route.apiKey);
  } else {
    headers.set("Authorization", `Bearer ${route.apiKey}`);
  }
  return headers;
}

function proxyRequest(
  endpoint: string,
  headers: Headers,
): { url: string; headers: Headers } {
  if (!isAllowedLlmUpstreamUrl(endpoint))
    throw new Error(
      `代理拒绝未授权上游：${safeHostname(endpoint) || endpoint}`,
    );
  const proxied = new Headers(headers);
  proxied.set("X-Upstream", endpoint);
  return { url: "/api/llm-proxy", headers: proxied };
}

function combineSignals(
  signal: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const timer = globalThis.setTimeout(
    () => controller.abort(new DOMException("LLM 请求超时", "TimeoutError")),
    timeoutMs,
  );
  const onAbort = () => controller.abort(signal?.reason ?? abortError());
  signal?.addEventListener("abort", onAbort, { once: true });
  return {
    signal: controller.signal,
    cleanup: () => {
      globalThis.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    },
  };
}

function retryAfterMs(headers: Headers): number | null {
  const raw = headers.get("retry-after");
  if (!raw) return null;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const timestamp = Date.parse(raw);
  return Number.isFinite(timestamp)
    ? Math.max(0, timestamp - Date.now())
    : null;
}

async function fetchAttempt(
  route: LlmRoute,
  endpoint: string,
  headers: Headers,
  body: unknown,
  signal?: AbortSignal,
): Promise<Response> {
  const attempt = async (useProxy: boolean) => {
    const target = useProxy
      ? proxyRequest(endpoint, headers)
      : { url: endpoint, headers };
    const timed = combineSignals(signal, route.timeoutMs ?? 120_000);
    try {
      return await fetch(target.url, {
        method: "POST",
        headers: target.headers,
        body: JSON.stringify(body),
        signal: timed.signal,
      });
    } finally {
      timed.cleanup();
    }
  };
  try {
    const direct = await attempt(false);
    if (direct.ok || !route.useProxy || direct.status < 500) return direct;
  } catch (error) {
    if (signal?.aborted) throw signal.reason ?? abortError();
    if (!route.useProxy) throw error;
  }
  return attempt(true);
}

function usageFrom(raw: Record<string, unknown> | undefined): LlmUsage | null {
  if (!raw) return null;
  const input = Number(raw.prompt_tokens ?? raw.input_tokens ?? 0);
  const output = Number(raw.completion_tokens ?? raw.output_tokens ?? 0);
  const total = Number(raw.total_tokens ?? input + output);
  return input || output || total
    ? { inputTokens: input, outputTokens: output, totalTokens: total }
    : null;
}

interface ParsedChunk {
  text: string;
  reasoning: string;
  usage: LlmUsage | null;
  responseId?: string;
}

function parseOpenAiChunk(
  value: Record<string, unknown>,
  apiStyle: LlmApiStyle,
): ParsedChunk {
  if (apiStyle === "responses") {
    const type = String(value.type ?? "");
    const delta = typeof value.delta === "string" ? value.delta : "";
    return {
      text: type.includes("output_text.delta") ? delta : "",
      reasoning:
        type.includes("reasoning") && type.endsWith(".delta") ? delta : "",
      usage: usageFrom(value.usage as Record<string, unknown> | undefined),
      responseId:
        typeof value.response_id === "string" ? value.response_id : undefined,
    };
  }
  const choices = value.choices as Array<Record<string, unknown>> | undefined;
  const delta = choices?.[0]?.delta as Record<string, unknown> | undefined;
  return {
    text: typeof delta?.content === "string" ? delta.content : "",
    reasoning:
      typeof delta?.reasoning_content === "string"
        ? delta.reasoning_content
        : "",
    usage: usageFrom(value.usage as Record<string, unknown> | undefined),
    responseId: typeof value.id === "string" ? value.id : undefined,
  };
}

function parseAnthropicChunk(value: Record<string, unknown>): ParsedChunk {
  const delta = value.delta as Record<string, unknown> | undefined;
  return {
    text:
      delta?.type === "text_delta" && typeof delta.text === "string"
        ? delta.text
        : "",
    reasoning:
      delta?.type === "thinking_delta" && typeof delta.thinking === "string"
        ? delta.thinking
        : "",
    usage: usageFrom(value.usage as Record<string, unknown> | undefined),
  };
}

function parseGeminiChunk(value: Record<string, unknown>): ParsedChunk {
  const candidates = value.candidates as
    Array<Record<string, unknown>> | undefined;
  const content = candidates?.[0]?.content as
    Record<string, unknown> | undefined;
  const parts = content?.parts as Array<Record<string, unknown>> | undefined;
  return {
    text:
      parts
        ?.filter((part) => part.thought !== true)
        .map((part) => (typeof part.text === "string" ? part.text : ""))
        .join("") ?? "",
    reasoning:
      parts
        ?.filter((part) => part.thought === true)
        .map((part) => (typeof part.text === "string" ? part.text : ""))
        .join("") ?? "",
    usage: usageFrom(
      value.usageMetadata as Record<string, unknown> | undefined,
    ),
  };
}

async function consumeSse(
  response: Response,
  provider: LlmProvider,
  apiStyle: LlmApiStyle,
  onToken?: (token: string) => void,
): Promise<ParsedChunk> {
  if (!response.body) throw new Error("LLM 返回空响应体");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "",
    text = "",
    reasoning = "",
    usage: LlmUsage | null = null,
    responseId: string | undefined;
  const consume = (line: string) => {
    const data = line.replace(/^data:\s*/, "").trim();
    if (!data || data === "[DONE]") return;
    try {
      const value = JSON.parse(data) as Record<string, unknown>;
      const parsed =
        provider === "anthropic"
          ? parseAnthropicChunk(value)
          : provider === "gemini"
            ? parseGeminiChunk(value)
            : parseOpenAiChunk(value, apiStyle);
      text += parsed.text;
      reasoning += parsed.reasoning;
      usage = parsed.usage ?? usage;
      responseId = parsed.responseId ?? responseId;
      if (parsed.text) onToken?.(parsed.text);
    } catch {
      /* providers may emit event-only SSE frames */
    }
  };
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() ?? "";
    for (const line of lines) if (line.startsWith("data:")) consume(line);
  }
  if (buffer.startsWith("data:")) consume(buffer);
  return { text, reasoning, usage, responseId };
}

function parseNonStreaming(
  response: Record<string, unknown>,
  provider: LlmProvider,
  apiStyle: LlmApiStyle,
): ParsedChunk {
  if (provider === "anthropic") {
    const content = response.content as
      Array<Record<string, unknown>> | undefined;
    return {
      text:
        content
          ?.filter((v) => v.type === "text")
          .map((v) => String(v.text ?? ""))
          .join("") ?? "",
      reasoning:
        content
          ?.filter((v) => v.type === "thinking")
          .map((v) => String(v.thinking ?? ""))
          .join("") ?? "",
      usage: usageFrom(response.usage as Record<string, unknown> | undefined),
      responseId: typeof response.id === "string" ? response.id : undefined,
    };
  }
  if (provider === "gemini") return parseGeminiChunk(response);
  if (apiStyle === "responses") {
    const output = response.output as
      Array<Record<string, unknown>> | undefined;
    const content =
      output?.flatMap(
        (item) =>
          (item.content as Array<Record<string, unknown>> | undefined) ?? [],
      ) ?? [];
    return {
      text: content
        .filter((v) => v.type === "output_text")
        .map((v) => String(v.text ?? ""))
        .join(""),
      reasoning: "",
      usage: usageFrom(response.usage as Record<string, unknown> | undefined),
      responseId: typeof response.id === "string" ? response.id : undefined,
    };
  }
  const choices = response.choices as
    Array<Record<string, unknown>> | undefined;
  const message = choices?.[0]?.message as Record<string, unknown> | undefined;
  return {
    text: typeof message?.content === "string" ? message.content : "",
    reasoning:
      typeof message?.reasoning_content === "string"
        ? message.reasoning_content
        : "",
    usage: usageFrom(response.usage as Record<string, unknown> | undefined),
    responseId: typeof response.id === "string" ? response.id : undefined,
  };
}

async function sleep(ms: number, signal?: AbortSignal) {
  await new Promise<void>((resolve, reject) => {
    const timer = globalThis.setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        globalThis.clearTimeout(timer);
        reject(signal.reason ?? abortError());
      },
      { once: true },
    );
  });
}

export async function callLLM(
  route: LlmRoute,
  messages: LlmMessage[],
  options: LlmCallOptions = {},
): Promise<LlmResult> {
  if (!route.apiKey.trim()) throw new Error("请先配置 LLM API Key");
  const provider = inferProvider(route),
    apiStyle = inferApiStyle(route, provider);
  const endpoint = endpointFor(route, provider, apiStyle),
    headers = buildHeaders(route, provider);
  const body =
    provider === "anthropic"
      ? buildAnthropicBody(route, messages, options)
      : provider === "gemini"
        ? buildGeminiBody(route, messages, options)
        : buildOpenAiBody(route, messages, apiStyle, options);
  let lastError: unknown;
  for (let attempt = 0; attempt <= (route.retries ?? 2); attempt++) {
    try {
      const response = await fetchAttempt(
        route,
        endpoint,
        headers,
        body,
        options.signal,
      );
      if (!response.ok) {
        const error = new LlmHttpError(
          response.status,
          await response.text(),
          retryAfterMs(response.headers),
        );
        if (!RETRYABLE_STATUS.has(response.status)) throw error;
        throw error;
      }
      const parsed =
        options.stream === false
          ? parseNonStreaming(
              (await response.json()) as Record<string, unknown>,
              provider,
              apiStyle,
            )
          : await consumeSse(response, provider, apiStyle, options.onToken);
      if (!parsed.text.trim())
        throw new LlmNoVisibleContentError(parsed.reasoning, parsed.responseId);
      return {
        text: parsed.text,
        reasoningText: parsed.reasoning,
        usage: parsed.usage,
        responseId: parsed.responseId,
        routeId: route.id ?? route.model,
        provider,
        apiStyle,
      };
    } catch (error) {
      if (options.signal?.aborted) throw options.signal.reason ?? abortError();
      lastError = error;
      const retryable =
        error instanceof LlmHttpError
          ? RETRYABLE_STATUS.has(error.status)
          : !(error instanceof LlmNoVisibleContentError);
      if (!retryable || attempt >= (route.retries ?? 2)) throw error;
      const delay =
        error instanceof LlmHttpError && error.retryAfterMs !== null
          ? error.retryAfterMs
          : Math.min(8_000, 600 * 2 ** attempt + Math.random() * 250);
      await sleep(delay, options.signal);
    }
  }
  throw lastError;
}

export async function callLLMWithRoutes(
  routes: LlmRoute[],
  messages: LlmMessage[],
  options: LlmCallOptions = {},
): Promise<LlmResult> {
  if (!routes.length) throw new Error("没有可用的 LLM 路由");
  const errors: string[] = [];
  for (const [routeIndex, route] of routes.entries()) {
    const startedAt = Date.now();
    const request = truncateDebugText(JSON.stringify(messages));
    try {
      const result = await callLLM(route, messages, options);
      if (options.debug) {
        const response = truncateDebugText(result.text);
        void putLlmDebugLog({
          id: crypto.randomUUID(),
          archiveName: options.debug.archiveName,
          turnId: options.debug.turnId,
          featureId: options.debug.featureId,
          routeId: route.id ?? route.model,
          host: safeHost(route.baseUrl),
          model: route.model,
          attempt: routeIndex + 1,
          status: "ok",
          startedAt,
          durationMs: Date.now() - startedAt,
          requestPreview: request.value,
          responsePreview: response.value,
          truncated: request.truncated || response.truncated,
          usage: result.usage
            ? {
                inputTokens: result.usage.inputTokens,
                outputTokens: result.usage.outputTokens,
                totalTokens: result.usage.totalTokens,
              }
            : undefined,
        }).catch(() => undefined);
      }
      return result;
    } catch (error) {
      if (options.signal?.aborted) throw error;
      if (options.debug) {
        void putLlmDebugLog({
          id: crypto.randomUUID(),
          archiveName: options.debug.archiveName,
          turnId: options.debug.turnId,
          featureId: options.debug.featureId,
          routeId: route.id ?? route.model,
          host: safeHost(route.baseUrl),
          model: route.model,
          attempt: routeIndex + 1,
          status: "error",
          startedAt,
          durationMs: Date.now() - startedAt,
          requestPreview: request.value,
          responsePreview: "",
          truncated: request.truncated,
          error: error instanceof Error ? error.message.slice(0, 2000) : String(error),
        }).catch(() => undefined);
      }
      errors.push(
        `${route.id ?? route.model}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }
  throw new AggregateError(errors, `所有 LLM 路由均失败：${errors.join("；")}`);
}

function safeHost(value: string) {
  try {
    return new URL(value).host;
  } catch {
    return "invalid-url";
  }
}

export async function callEmbedding(
  route: EmbeddingRoute,
  input: string | string[],
  signal?: AbortSignal,
): Promise<number[][]> {
  if (!route.apiKey.trim()) throw new Error("未配置 embedding API Key");
  const endpoint = joinUrl(route.baseUrl, "/embeddings");
  const headers = new Headers({
    "Content-Type": "application/json",
    Authorization: `Bearer ${route.apiKey}`,
  });
  const target = route.useProxy
    ? proxyRequest(endpoint, headers)
    : { url: endpoint, headers };
  const timed = combineSignals(signal, route.timeoutMs ?? 60_000);
  try {
    const response = await fetch(target.url, {
      method: "POST",
      headers: target.headers,
      body: JSON.stringify({
        model: route.model,
        input,
        dimensions: route.dimensions,
      }),
      signal: timed.signal,
    });
    if (!response.ok)
      throw new LlmHttpError(
        response.status,
        await response.text(),
        retryAfterMs(response.headers),
      );
    const payload = (await response.json()) as {
      data?: Array<{ index: number; embedding: number[] }>;
    };
    const rows = [...(payload.data ?? [])]
      .sort((a, b) => a.index - b.index)
      .map((row) => row.embedding);
    if (
      !rows.length ||
      rows.some(
        (row) =>
          !Array.isArray(row) || row.some((value) => !Number.isFinite(value)),
      )
    )
      throw new Error("embedding 返回格式无效");
    return rows;
  } finally {
    timed.cleanup();
  }
}

export async function streamChat(
  settings: LlmSettings,
  messages: LlmMessage[],
  onToken: (token: string) => void,
  signal?: AbortSignal,
) {
  const result = await callLLM(settings, messages, {
    signal,
    stream: true,
    onToken,
  });
  return result.text;
}

export async function fetchUpstream(
  url: string,
  init: RequestInit,
  useProxy: boolean,
): Promise<Response> {
  if (!useProxy) return fetch(url, init);
  const headers = new Headers(init.headers);
  const target = proxyRequest(url, headers);
  return fetch(target.url, { ...init, headers: target.headers });
}
